import dayjs from 'dayjs';
import * as R from 'ramda';
import { RRule, rrulestr } from 'rrule';
import { newPortalDate, PortalDate } from './PortalDate';
import {
  CreateScheduledEventMetadataInput,
  EventTriggerType,
  UpdateScheduledEventMetadataInput,
  CreateScheduledHolidayMetadataInput,
  UpdateScheduledHolidayMetadataInput,
  HolidayExecutionType,
  JobExecutionStatus,
  TaskExecutionStatus,
} from '@/API';
import { ScheduledHolidayMetadataLabel } from '@/appsync/graphql/scheduledHolidayMetadata';
import { TaskHistory } from '@/appsync/graphql/taskHistory';
import {
  createScheduledEventMetadata,
  updateScheduledEventMetadata,
} from '@/appsync/service/ScheduledEventMetadataMutationService';
import {
  createScheduledHolidayMetadata,
  updateScheduledHolidayMetadata,
} from '@/appsync/service/ScheduledHolidayMetadataMutationService';
import {
  CalendarEvent,
  ErrorInfo,
  LoginInfo,
  ScheduledEventMetadataWithDetailForFront,
  ScheduledHolidayMetadataWithDetailForFront,
} from '@/types/type';
import {
  boolean2_BooleanEnum,
  getEndDayOfThisMonth,
  getFirstDayOfThisMonth,
  getSHA3OfTenantLandscape,
  isNotEmptyArray,
  wrapNilInput,
} from '@/utils/CommonUtils';
import { isRelatedToToday } from '@/utils/EventUtils';
import {
  getDateInUTCWithSameDateTime,
  getFormattedDateWithoutTime,
  getFormattedDateWithoutTimeInUtc,
  getRRuleSet,
  getRRuleSetArgs,
} from '@/utils/RRuleUtils';
import { Day, TriggerOnHolidays, ICON } from '@/utils/consts';

const END_OF_DAY = '23:59:59';
const RELEASE_DAY_OF_JOB_SCHEDULER = '2021/12/22';

export const FAILED_TO_ADJUST_DATE_EVENT_ID_KEY = 'failedToAdjustDateEventId';
export const DATE_NOT_ADJUSTED = 'date_not_adjusted';

export enum EventType {
  PublicHoliday,
  CustomHoliday,
  NormalEvent,
}

export enum Direction {
  Forward = 1,
  Backward = -1,
}

export const returnHeadIfNotEmptyArrayElseNull = R.ifElse(
  isNotEmptyArray,
  R.head,
  R.always(null)
);

export const flattenLabels: (
  xs: ScheduledHolidayMetadataLabel[]
) => string[] = R.pipe(R.pluck('labels'), R.flatten);

export const returnEmptyArrayIfNilInputOrElseProcess: (
  onFalse: (x: any) => any
) => (x: any) => any = wrapNilInput(R.always([]));

export const endScheduleToday = (metadataItem: any) => {
  const today = new Date();
  const startOfToday = dayjs().startOf('day');
  const now = new Date();
  const formattedToday = newPortalDate(dayjs(), 'YYYY-MM-DD').toString();
  const executionTimeForToday = new Date(
    R.join(' ', [formattedToday, metadataItem.executionTime])
  );
  const dayBefore = newPortalDate(dayjs())
    .subtract(1, 'day')
    .toDate();

  const todayOrDayBefore =
    executionTimeForToday <= now
      ? getFormattedDateWithoutTime(today)
      : getFormattedDateWithoutTime(dayBefore);

  const newEndDate =
    dayjs(metadataItem.dateTo) < startOfToday
      ? metadataItem.dateTo
      : todayOrDayBefore;

  const updatedDateToToToday = R.assoc('dateTo', newEndDate)(metadataItem);
  const rruleSetArgs = getRRuleSetArgs(updatedDateToToToday);
  return R.assoc('rruleSet', getRRuleSet(rruleSetArgs))(updatedDateToToToday);
};

export const updateDateFromForCurrentEventMetadata = (metadataItem: any) => {
  const now = new Date();
  const formattedToday = newPortalDate(dayjs(), 'YYYY-MM-DD').toString();
  const executionTimeForToday = new Date(
    R.join(' ', [formattedToday, metadataItem.executionTime])
  );

  const updatedDateFrom =
    executionTimeForToday <= now
      ? R.assoc(
          'dateFrom',
          getFormattedDateWithoutTime(
            newPortalDate(dayjs())
              .add(1, 'day')
              .toDate()
          )
        )(metadataItem)
      : R.assoc(
          'dateFrom',
          getFormattedDateWithoutTime(dayjs().toDate())
        )(metadataItem);

  const rruleSetArgs = getRRuleSetArgs(updatedDateFrom);

  return R.assoc('rruleSet', getRRuleSet(rruleSetArgs))(updatedDateFrom);
};

export const callCreateScheduledEventMetadata = async (
  data: ScheduledEventMetadataWithDetailForFront,
  loginInfo: LoginInfo
) => {
  const rruleSetArgs = getRRuleSetArgs(data);

  const createScheduledEventMetadataInput: CreateScheduledEventMetadataInput = {
    id: data.id,
    tenant: loginInfo.tenant,
    landscape: loginInfo.landscape,
    tenantLandscape: getSHA3OfTenantLandscape(
      loginInfo.tenant,
      loginInfo.landscape
    ),
    title: data.title,
    description: data.description,
    jobId: data.job ? data.job.id : '',
    repeatType: data.repeatType,
    rruleSet: getRRuleSet(rruleSetArgs),
    holidayExecutionType: data.holidayExecutionType,
    useCustomHoliday: boolean2_BooleanEnum(
      data.triggerOnHolidays === TriggerOnHolidays.customHolidays
    ),
    customHolidayLabels: data.customHolidayLabels,
    eventTriggerType: EventTriggerType.Scheduled,
    createdBy: loginInfo.loginName,
    updatedBy: loginInfo.loginName,
    editedAt: new Date().toISOString(),
  };

  await createScheduledEventMetadata(createScheduledEventMetadataInput);
};

export const callUpdateScheduledEventMetadata = async (
  data: ScheduledEventMetadataWithDetailForFront,
  loginInfo: LoginInfo
) => {
  const rruleSetArgs = getRRuleSetArgs(data);

  const updateScheduledEventMetadataInput: UpdateScheduledEventMetadataInput = {
    id: data.id,
    title: data.title,
    description: data.description,
    jobId: data.job ? data.job.id : '',
    repeatType: data.repeatType,
    rruleSet: getRRuleSet(rruleSetArgs),
    holidayExecutionType: data.holidayExecutionType,
    useCustomHoliday: boolean2_BooleanEnum(
      data.triggerOnHolidays === TriggerOnHolidays.customHolidays
    ),
    customHolidayLabels: data.customHolidayLabels,
    eventTriggerType: EventTriggerType.Scheduled,
    updatedBy: loginInfo.loginName,
    editedAt: new Date().toISOString(),
  };

  await updateScheduledEventMetadata(updateScheduledEventMetadataInput);
};

export const callCreateScheduledHolidayMetadata = async (
  data: ScheduledHolidayMetadataWithDetailForFront,
  loginInfo: LoginInfo
) => {
  const rruleSetArgs = getRRuleSetArgs(data);

  const createScheduledHolidayMetadataInput: CreateScheduledHolidayMetadataInput = {
    id: data.id,
    tenant: loginInfo.tenant,
    landscape: loginInfo.landscape,
    tenantLandscape: getSHA3OfTenantLandscape(
      loginInfo.tenant,
      loginInfo.landscape
    ),
    title: data.title,
    description: data.description,
    repeatType: data.repeatType,
    rruleSet: getRRuleSet(rruleSetArgs),
    labels: data.labels,
    createdBy: loginInfo.loginName,
    updatedBy: loginInfo.loginName,
  };
  await createScheduledHolidayMetadata(createScheduledHolidayMetadataInput);
};

export const callUpdateScheduledHolidayMetadata = async (
  data: ScheduledHolidayMetadataWithDetailForFront,
  loginInfo: LoginInfo
) => {
  const rruleSetArgs = getRRuleSetArgs(data);

  const updateScheduledHolidayMetadataInput: UpdateScheduledHolidayMetadataInput = {
    id: data.id,
    title: data.title,
    description: data.description,
    repeatType: data.repeatType,
    rruleSet: getRRuleSet(rruleSetArgs),
    labels: data.labels,
    updatedBy: loginInfo.loginName,
  };

  await updateScheduledHolidayMetadata(updateScheduledHolidayMetadataInput);
};

export const isDateFromAfterToday = (metadata: any): boolean => {
  const dateFromInJST = dayjs(metadata.dateFrom);
  return dateFromInJST > dayjs().startOf('day');
};

export const firstDayOfMonth = (date: string) =>
  dayjs(date)
    .startOf('month')
    .toDate();

export const lastDayOfMonth = (date: string) =>
  dayjs(date)
    .endOf('month')
    .toDate();

export const occurrencesForTargetMonth = (
  rruleString: string,
  focusDate: string
) => {
  const rrule = rrulestr(rruleString);
  const firstDayForAllEvents = new Date(RELEASE_DAY_OF_JOB_SCHEDULER);
  const lastDayOfMonthWith3MonthPadding = newPortalDate(
    dayjs(lastDayOfMonth(focusDate))
  )
    .add(3, 'month')
    .toDate();

  const occurrences = rrule.between(
    firstDayForAllEvents,
    lastDayOfMonthWith3MonthPadding,
    true
  );

  const timeOffset = new Date(Date.now()).getTimezoneOffset();
  const diffBetweenTimeZoneOfRRuleAndLocal = 0; //TODO: Add support for other timezone

  return occurrences.map(date =>
    newPortalDate(dayjs(date))
      .add(timeOffset, 'minute')
      .add(diffBetweenTimeZoneOfRRuleAndLocal, 'minute')
      .toDate()
  );
};

const getAvailableDate = (
  direction: Direction,
  targetEventDate: string,
  holidayDateList: string[]
) => {
  const isIncluded = R.includes(R.__, holidayDateList);

  let count = 0;
  while (count < 1000) {
    const isTargetDateIncluded = isIncluded(targetEventDate.split(' ')[0]);
    if (!isTargetDateIncluded) break;

    targetEventDate = newPortalDate(
      dayjs(targetEventDate),
      'YYYY-MM-DD HH:mm:ss'
    )
      .add(direction, 'day')
      .toString();
    count++;
  }

  if (count === 1000) {
    return DATE_NOT_ADJUSTED;
  }
  return targetEventDate;
};

export const getAvailableDateCurried = R.curry(getAvailableDate);

const getPreviousAvailableDate = getAvailableDateCurried(Direction.Backward);

const getNextAvailableDate = getAvailableDateCurried(Direction.Forward);

const changeStartTo = R.assoc('start');

export const isOverlappingWithHoliday = (holidayDateList: string[]) =>
  R.pipe(
    R.path<any>(['start']),
    R.split(' '),
    R.head,
    R.includes(R.__, holidayDateList)
  );

export const filterHolidayDates = (
  targetEvent: CalendarEvent,
  holidays: CalendarEvent[]
) => {
  const getStartDates = R.pluck('start');
  if (targetEvent.triggerOnHolidays === TriggerOnHolidays.allHolidays) {
    return getStartDates(holidays);
  } else {
    const targetLabels = R.defaultTo([], targetEvent.labels);
    const targetHolidays = R.filter(
      R.pipe(
        R.path<any>(['labels']),
        R.any(R.includes(R.__, targetLabels))
      )
    )(holidays);

    return getStartDates(targetHolidays);
  }
};

export const makeAdjustmentsForSkip = (
  targetEvent: CalendarEvent,
  holidays: CalendarEvent[]
) => {
  const targetHolidayDates = filterHolidayDates(
    targetEvent,
    holidays
  ) as string[];
  return isOverlappingWithHoliday(targetHolidayDates)(targetEvent)
    ? null
    : targetEvent;
};

export const makeAdjustmentsForTriggerBeforeAfter = (
  targetEvent: CalendarEvent,
  holidays: CalendarEvent[]
) => {
  const targetHolidayDates = filterHolidayDates(
    targetEvent,
    holidays
  ) as string[];

  const isOverlappingWithAnyHoliday = isOverlappingWithHoliday(
    targetHolidayDates
  )(targetEvent);

  if (!isOverlappingWithAnyHoliday) {
    return targetEvent;
  } else if (
    targetEvent.holidayExecutionType ===
    HolidayExecutionType.TriggerAfterHoliday
  ) {
    const nextAvailableDate = getNextAvailableDate(
      targetEvent.start,
      targetHolidayDates
    );
    if (nextAvailableDate === DATE_NOT_ADJUSTED) {
      return { [FAILED_TO_ADJUST_DATE_EVENT_ID_KEY]: targetEvent.eventId };
    }
    return changeStartTo(nextAvailableDate)(targetEvent);
  } else if (
    targetEvent.holidayExecutionType ===
    HolidayExecutionType.TriggerBeforeHoliday
  ) {
    const previousAvailableDate = getPreviousAvailableDate(
      targetEvent.start,
      targetHolidayDates
    );
    if (previousAvailableDate === DATE_NOT_ADJUSTED) {
      return { [FAILED_TO_ADJUST_DATE_EVENT_ID_KEY]: targetEvent.eventId };
    }
    return changeStartTo(previousAvailableDate)(targetEvent);
  }

  return targetEvent;
};

export const makeAdjustments = (
  targetEvent: CalendarEvent,
  holidays: CalendarEvent[]
) => {
  if (targetEvent.holidayExecutionType === HolidayExecutionType.NoChange) {
    return targetEvent;
  } else if (targetEvent.holidayExecutionType === HolidayExecutionType.Skip) {
    return makeAdjustmentsForSkip(targetEvent, holidays);
  } else {
    return makeAdjustmentsForTriggerBeforeAfter(targetEvent, holidays);
  }
};

export const getSpecifiedPeriodItems = (
  list: CalendarEvent[],
  startDateTime: PortalDate,
  endDateTime: PortalDate
) => {
  return list.filter(
    item =>
      item &&
      new Date(item.start) >= startDateTime.toDate() &&
      new Date(item.start) <= endDateTime.toDate()
  );
};

export const getTargetMonthItems = (
  list: CalendarEvent[],
  focusDate: string
) => {
  return list.filter(
    item =>
      item &&
      new Date(item.start) >= firstDayOfMonth(focusDate) &&
      new Date(item.start) <= lastDayOfMonth(focusDate)
  );
};

export const throwRetryFailedError = (
  eventId: string | undefined,
  eventHistoryId: string | undefined
) => {
  const errorInfo: ErrorInfo = {
    code: 'E00010',
    title: 'Retry action failed',
    message: `Retry action failed because there seems to be some problem with
    the data for event with eventId - ${R.defaultTo(
      '',
      eventId
    )} and eventHistoryId - ${R.defaultTo('', eventHistoryId)}.
    Please contact the administrator.`,
  };
  throw new Error(JSON.stringify(errorInfo));
};

export const filterDuplicatedEvents = (events: CalendarEvent[]) => {
  return events.filter(
    (element, index, self) =>
      self.findIndex(
        e => e.eventId === element.eventId && e.start === element.start
      ) === index
  );
};

export const taskIconColor = (taskHistory: TaskHistory): string => {
  if (!taskHistory.taskExecutionStatus) {
    return '';
  }
  switch (taskHistory.taskExecutionStatus) {
    case TaskExecutionStatus.Running:
      return ICON.RUNNING.COLOR;
    case TaskExecutionStatus.Skipped:
      return ICON.SKIPPED.COLOR;
    case TaskExecutionStatus.Failed:
      return ICON.FAILED.COLOR;
    case TaskExecutionStatus.Succeeded:
      return ICON.SUCCEEDED.COLOR;
    default: {
      return '';
    }
  }
};

export const taskIcon = (taskHistory: TaskHistory): string => {
  if (!taskHistory.taskExecutionStatus) {
    return '';
  }
  switch (taskHistory.taskExecutionStatus) {
    case TaskExecutionStatus.Running:
      return ICON.RUNNING.NAME;
    case TaskExecutionStatus.Skipped:
      return ICON.SKIPPED.NAME;
    case TaskExecutionStatus.Failed:
      return ICON.FAILED.NAME;
    case TaskExecutionStatus.Succeeded:
      return ICON.SUCCEEDED.NAME;
    default: {
      return '';
    }
  }
};

export const eventIcon = (status: JobExecutionStatus): string => {
  switch (status) {
    case JobExecutionStatus.NotTriggered:
      return ICON.NOT_TRIGGERED.NAME;
    case JobExecutionStatus.Running:
      return ICON.RUNNING.NAME;
    case JobExecutionStatus.Aborted:
      return ICON.ABORTED.NAME;
    case JobExecutionStatus.Failed:
      return ICON.FAILED.NAME;
    case JobExecutionStatus.Succeeded:
      return ICON.SUCCEEDED.NAME;
    default: {
      return '';
    }
  }
};

export const eventIconColor = (status: JobExecutionStatus): string => {
  switch (status) {
    case JobExecutionStatus.NotTriggered:
      return ICON.NOT_TRIGGERED.COLOR;
    case JobExecutionStatus.Running:
      return ICON.RUNNING.COLOR;
    case JobExecutionStatus.Aborted:
      return ICON.ABORTED.COLOR;
    case JobExecutionStatus.Failed:
      return ICON.FAILED.COLOR;
    case JobExecutionStatus.Succeeded:
      return ICON.SUCCEEDED.COLOR;
    default: {
      return '';
    }
  }
};
